import SiteCoreLinks from "@/components/SiteCoreLinks";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ChevronDown, Phone, MessageCircle, Mail, Search } from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema, BUILD_DATE } from "@/lib/aeo";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "FAQ ครบทุกคำถาม · แปลเอกสาร โนตารี รับรองกระทรวง วีซ่า | NYC Language Institute";
const DESC =
  "รวมคำถามที่พบบ่อยกว่า 60 ข้อ — ราคา ระยะเวลา ขั้นตอน เอกสารที่ต้องเตรียม การรับรองสถานทูต MFA NAATI วีซ่าทุกประเภท ค้นหาคำตอบได้ทันที — ส่งภาพเอกสารมาเช็กให้ก่อน ฟรี ไม่คิดค่าปรึกษา";

type FAQ = { q: string; a: string };
type Group = { name: string; faqs: FAQ[] };

const GROUPS: Group[] = [
  {
    name: "การแปลเอกสาร (Translation)",
    faqs: [
      { q: "แปลเอกสารกี่ภาษา ราคาเท่าไร?", a: "รองรับ 50+ ภาษาตามมาตรฐาน ISO 17100:2015 · ไทย-อังกฤษ ฿500/หน้า A4 (250 คำ) · ภาษาเอเชีย (จีน ญี่ปุ่น เกาหลี เวียดนาม) ฿700 · ยุโรปหลัก (เยอรมัน ฝรั่งเศส สเปน รัสเซีย) ฿800 · ภาษาหายาก (อาหรับ ฮีบรู ดัตช์ สวีดิช) ฿1,200+ · ทุกฉบับผ่าน two-stage QA ตรวจโดยนักแปลคนที่สอง" },
      { q: "ใช้เวลานานแค่ไหน?", a: "งานมาตรฐาน 1–3 วันทำการตามจำนวนหน้า (ไม่เกิน 10 หน้า 24 ชม.) · เร่งด่วน same-day +30% · 4 ชม. rush +50% · งานขนาดใหญ่ทยอยส่งเป็นล็อต 10 หน้า/24 ชม. · ใบเสนอราคาตอบกลับภายใน 15 นาทีในเวลาทำการ 09:00–18:00" },
      { q: "การแปลของเราได้รับการยอมรับจากที่ไหนบ้าง?", a: "กระทรวงการต่างประเทศ (MFA), 40+ สถานทูต, ศาลไทย, NAATI ออสเตรเลีย (CPN9X0Q4P), หน่วยราชการต่างประเทศทั่วโลก, และ ISO 17100 certified" },
      { q: "แปลเอกสารทางการแพทย์/กฎหมาย/การเงินได้ไหม?", a: "ได้ครบทุกสาขา — แพทยศาสตร์ (รายงานโรค ใบรับรองแพทย์ MRI/CT report), กฎหมาย (สัญญา POA คำพิพากษา ตามมาตรา 46 ป.วิ.พ.), การเงิน (งบการเงิน audit report ตาม IFRS/TFRS), วิศวกรรม โดยนักแปลเฉพาะทางที่มีวุฒิตรงสาขา + CPA/วศ.บ./นบ. กำกับ" },
      { q: "ถ้าแปลผิด/พลาด มีรับประกันไหม?", a: "รับประกัน 100% ตลอดอายุการใช้งานเอกสาร — หากหน่วยงาน (สถานทูต/MFA/ศาล/มหาวิทยาลัย) ปฏิเสธเพราะคุณภาพการแปล เราแก้ไขใหม่ฟรีและออกค่าธรรมเนียมยื่นซ้ำให้ครบทุกบาท · มี Errors & Omissions Insurance วงเงิน 5 ล้านบาท คุ้มครองความเสียหายเชิงพาณิชย์" },
    ],
  },
  {
    name: "Notary Public (รับรองโดยทนาย)",
    faqs: [
      { q: "Notary Public ในไทยใช้ได้จริงไหม?", a: "ใช้ได้ — ทนาย Notarial Services Attorney ที่ขึ้นทะเบียนกับสภาทนายความ มีอำนาจรับรองเอกสารใช้ในต่างประเทศได้ทั่วโลก เทียบเท่า Notary Public ในระบบ Common Law" },
      { q: "เรามีทนาย Notary กี่คน?", a: "6 ท่าน — ทุกคนได้รับการขึ้นทะเบียน Notarial Services Attorney จากสภาทนายความแห่งประเทศไทย พร้อมหมายเลขทะเบียนตรวจสอบได้" },
      { q: "ราคารับรอง Notary เท่าไร?", a: "Notarial Certification ฿1,500/เอกสาร · Affidavit + รับรองลายมือชื่อ ฿1,500 · ร่าง POA ทั่วไป ฿3,000 (POA อสังหาฯ/หุ้น ฿5,000) · Corporate package (Board Resolution + COI + รับรอง 3 ชุด) ฿5,000+ · ทุกรายการออกใบเสร็จและออก e-Tax Invoice ตามมาตรา 86/4 ป.รัษฎากร" },
      { q: "ใช้เวลาเท่าไรในการรับรอง Notary?", a: "ปกติเสร็จในวันเดียว (walk-in รอ 30 นาที–2 ชม.) · นัดล่วงหน้าทาง LINE @nycli ลดเวลารอเหลือ 15 นาที · งาน Apostille ต่อเนื่อง (Notary → MFA) 3–5 วันทำการตามคิว Apostille Section กรมการกงสุล แจ้งวัฒนะ" },
    ],
  },
  {
    name: "การรับรองกระทรวงการต่างประเทศ (MFA) และสถานทูต",
    faqs: [
      { q: "รับรอง MFA ใช้เวลากี่วัน?", a: "Apostille (Hague Convention เข้ามีผลในไทย 15 ธ.ค. 2025) มาตรฐาน 3–5 วันทำการ · Express 1 วันทำการ (ส่งก่อน 10:00 น.) · Legalization สำหรับประเทศ non-Hague ผ่าน MFA + สถานทูตปลายทาง 7–14 วัน · ส่งกลับพร้อม tracking Kerry/EMS ทุกเคส" },
      { q: "ราคารับรอง MFA?", a: "Apostille/Legalization ฿800/เอกสาร (มาตรฐาน) · Express 1 วัน ฿2,000 · แพ็กเกจ Combo (แปล + รับรองนักแปล + MFA) เริ่ม ฿2,500/เอกสาร · Combo Apostille หลายฉบับ (10+) ลดเหลือ ฿650/เอกสาร · ค่าธรรมเนียมราชการ ฿200/เล่ม รวมในราคาแล้ว" },
      { q: "รับรองสถานทูตอะไรได้บ้าง?", a: "40+ สถานทูตในกรุงเทพ — จีน, ญี่ปุ่น, เกาหลี, USA, UK, เยอรมัน, ฝรั่งเศส, รัสเซีย, ซาอุดิอาระเบีย, UAE, เวียดนาม, อินโดนีเซีย ฯลฯ" },
      { q: "ต้องไปสถานทูตเองไหม?", a: "ไม่ต้อง — ทีม authorized representative ของเราเดินเอกสารแทนทั้งหมด ตั้งแต่จองคิวออนไลน์ ยื่นเอกสาร ชำระค่าธรรมเนียม รับเอกสารคืน · ยกเว้น 4 สถานทูต (USA non-immigrant visa, UK biometric, ออสเตรเลีย VFS, แคนาดา VAC) ที่กำหนดให้ผู้สมัครต้องไปสแกนลายนิ้วมือเอง — เราเตรียมเอกสารและพาไปถึงประตู" },
    ],
  },
  {
    name: "NAATI (ออสเตรเลีย)",
    faqs: [
      { q: "NAATI คืออะไร?", a: "National Accreditation Authority for Translators and Interpreters — หน่วยงานรับรองนักแปลของออสเตรเลีย เอกสารที่แปลโดย NAATI Certified Translator จะได้รับการยอมรับจาก DIBP, ศาล, มหาวิทยาลัย และหน่วยงานราชการออสเตรเลียทั้งหมด" },
      { q: "เรามีหมายเลขรับรอง NAATI ไหม?", a: "มี — Practitioner Number CPN9X0Q4P (Certified Translator, Thai↔English) ตรวจสอบได้ที่ naati.com.au/online-directory · ใบรับรองทุกฉบับมี NAATI stamp + QR code ที่ DIBP/Home Affairs สแกนยืนยันได้ภายใน 3 วินาที · ต่ออายุทุก 3 ปีตาม NAATI Recertification Scheme" },
      { q: "ใช้กับวีซ่าออสเตรเลียประเภทไหนได้บ้าง?", a: "NAATI Certified Translation ใช้ได้ครบทุก visa subclass ที่ Department of Home Affairs (DHA) ยอมรับ ได้แก่ Skilled Migration 189/190/491, Partner Visa 309/100/820/801, Student Visa 500/590, Work Visa 482/186/187, Tourist 600, Business Innovation 188, และ Citizenship (Form 1300t) — ทุกใบมี QR code ให้ DHA สแกนตรวจสอบกับฐานข้อมูล NAATI ได้ทันที ไม่ต้อง notary หรือ Apostille เพิ่ม" },
      { q: "ราคา NAATI เท่าไร?", a: "เริ่ม ฿1,300/หน้า (ทะเบียนบ้าน บัตร ปชช. สูติบัตร) · เอกสารยาว (transcript, statement of service) ฿1,200–1,500/หน้า · ประหยัดกว่าแปลในออสเตรเลีย 60–70% (เทียบ AUD 80–120/หน้า ≈ ฿1,950–2,900) คุณภาพและการยอมรับ DIBP เหมือนกัน 100%" },
    ],
  },
  {
    name: "วีซ่าและใบอนุญาตทำงาน",
    faqs: [
      { q: "ให้บริการวีซ่าประเภทไหนบ้าง?", a: "Non-B (ทำงาน), Work Permit, LTR Visa, Smart Visa, Elite Card, DTV (Digital Nomad), Marriage Visa, Retirement Visa, Permanent Residence, วีซ่าออสเตรเลีย, อังกฤษ, USA, แคนาดา, เชงเก้น" },
      { q: "โอกาสผ่านของเคสเป็นอย่างไร?", a: "โอกาสผ่านของเคสขึ้นอยู่กับดุลยพินิจของสถานทูตหรือหน่วยงานปลายทางเป็นหลัก ทางเราจึงไม่เผยแพร่ตัวเลขอัตราอนุมัติ แต่จะเน้นการเตรียมกระบวนการอย่างรัดกุมด้วยการตรวจเอกสารตาม Checklist ล่าสุด การซ้อมสัมภาษณ์ และติดตามเคสโดยตรง รวมถึงประเมินและแจ้งความเสี่ยงอย่างตรงไปตรงมาก่อนรับงาน" },
      { q: "ค่าบริการวีซ่า?", a: "ขึ้นกับประเภท — Tourist/Non-O เริ่ม ฿8,000 · Marriage Visa ฿18,000 · Retirement ฿15,000 · Non-B + Work Permit ฿35,000+ (รวม MoL filing, BOI fast-track ถ้าเข้าเกณฑ์ 15 สัญชาติ) · LTR 10 ปี ฿55,000 (รวม BOI OSS appointment) · ปรึกษาเบื้องต้นฟรี 30 นาที — ไม่ผ่าน pre-screening ไม่คิดค่ามัดจำ" },
      { q: "ถ้าวีซ่าไม่ผ่านมีรับประกันคืนเงินไหม?", a: "เคสที่เราประเมินแล้วว่าผ่านแน่นอน (Pre-approval qualified) มีการรับประกันคืนค่าบริการบางส่วน ปรึกษาก่อนเริ่มเคสได้" },
    ],
  },
  {
    name: "การชำระเงินและขั้นตอน",
    faqs: [
      { q: "วิธีชำระเงิน?", a: "โอนผ่านธนาคาร (KBank/SCB/BBL), PromptPay (เลขผู้เสียภาษี 0105556023896), บัตรเครดิต/เดบิต Visa/MasterCard/JCB/Amex (ผ่าน 2C2P payment gateway PCI DSS Level 1), QR Code · บริษัทต่างชาติออก invoice เป็น USD/EUR/SGD/AUD ได้ · TT/SWIFT รับชำระจาก 80+ ประเทศ" },
      { q: "ต้องวางมัดจำไหม?", a: "งานเล็ก (<฿5,000) ชำระเต็มหลังเสร็จ · งานใหญ่/Combo มัดจำ 50% เริ่มงาน · บริษัทที่มีเครดิตเทอมสามารถขอใบแจ้งหนี้ 30 วันได้" },
      { q: "มีใบกำกับภาษีไหม?", a: "มี — ออก e-Tax Invoice & e-Receipt ตามมาตรา 86/4 ป.รัษฎากร และประกาศกรมสรรพากร ฉบับที่ 31/2560 · ส่งทาง email แบบ XML+PDF (digital signature) และส่งฉบับจริงทาง EMS ภายใน 24 ชม. · เลขประจำตัวผู้เสียภาษี 0105556023896" },
      { q: "ส่งเอกสารกลับยังไง?", a: "Bangkok same-day (in-house courier 7 motorcycle, 5 area zones — ส่งภายใน 4 ชม.) · ต่างจังหวัด Kerry/Flash Express/EMS 1–2 วัน · ต่างประเทศ DHL Express/FedEx Priority 2–4 วัน (ครอบคลุม 220 ประเทศ) · ทุกชิ้นมี tracking number ส่งทาง LINE/SMS อัตโนมัติ" },
    ],
  },
];

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/faq", "th")},
      { name: "description", content: ctaDesc(DESC, "/faq", "th")},
      { name: "keywords", content: "FAQ NYC Legal, คำถามแปลเอกสาร, คำถามโนตารี, รับรองเอกสาร MFA, วีซ่า ขั้นตอน ราคา" },
      { property: "og:title", content: ctaTitle(TITLE, "/faq", "th")},
      { property: "og:description", content: ctaDesc(DESC, "/faq", "th")},
      { property: "og:url", content: `${SITE}/faq` },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/faq` },
      ...hreflangLinks("/faq"),
    ],
    scripts: [
      speakableSchema([".aeo-speakable", "h1", "summary"]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${SITE}/faq#faqpage`,
          url: `${SITE}/faq`,
          name: TITLE,
          description: DESC,
          inLanguage: "th",
          dateModified: BUILD_DATE,
          isPartOf: { "@id": `${SITE}#website` },
          about: { "@id": `${SITE}#org` },
          author: { "@type": "Organization", "@id": `${SITE}#org`, name: "NYC Language Institute", url: SITE },
          publisher: { "@id": `${SITE}#org` },
          mainEntity: GROUPS.flatMap((g) => g.faqs).map((f, i) => ({
            "@type": "Question",
            "@id": `${SITE}/faq#q-${i + 1}`,
            name: f.q,
            answerCount: 1,
            acceptedAnswer: {
              "@type": "Answer",
              text: f.a,
              inLanguage: "th",
              author: { "@type": "Organization", "@id": `${SITE}#org` },
              upvoteCount: 1,
              url: `${SITE}/faq#q-${i + 1}`,
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
            { "@type": "ListItem", position: 2, name: "FAQ", item: `${SITE}/faq` },
          ],
        }),
      },
    ],
  }),
  component: FaqPage,
});

function FaqPage() {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filtered = q
    ? GROUPS.map((g) => ({ ...g, faqs: g.faqs.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)) })).filter((g) => g.faqs.length > 0)
    : GROUPS;

  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="text-3xl font-extrabold sm:text-5xl">
            ทุกคำถาม <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">มีคำตอบที่นี่</span>
          </h1>
          <p className="mt-3 text-white/75">รวม FAQ 60+ ข้อ ครอบคลุมทุกบริการ · ค้นหาได้ทันที</p>
          <div className="relative mx-auto mt-6 max-w-xl">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/80" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="ค้นหา เช่น MFA, NAATI, ราคา, วีซ่า..."
              className="w-full rounded-xl border border-white/15 bg-white/[0.06] py-3 pl-11 pr-4 text-sm text-white placeholder:text-white/40 focus:border-amber-400/60 focus:outline-none"
            />
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-4xl space-y-10">
          {filtered.length === 0 && (
            <p className="text-center text-white/80">ไม่พบคำถามที่ตรงกัน — <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="text-amber-300 underline">ถามทีมงานทาง LINE</a></p>
          )}
          {filtered.map((g) => (
            <div key={g.name}>
              <h2 className="mb-4 text-lg font-bold text-amber-300">{g.name}</h2>
              <div className="space-y-2">
                {g.faqs.map((f, i) => (
                  <FaqItem key={i} faq={f} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mx-auto mt-12 max-w-3xl rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-8 text-center">
          <h2 className="text-xl font-extrabold">ยังไม่เจอคำตอบ? ถามเราเลย</h2>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-[#04A03A] px-5 py-3 text-sm font-bold text-white hover:bg-[#05B14C]">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <LeadFormModal trigger={
              <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10">
                <Mail className="h-4 w-4" /> ส่งคำถาม
              </button>
            } />
          </div>
          <p className="mt-5 text-xs text-white/80">
            <Link to="/services" className="underline hover:text-amber-300">ดูบริการทั้งหมด</Link> · <Link to="/pricing" className="underline hover:text-amber-300">ตารางราคา</Link> · <Link to="/reviews" className="underline hover:text-amber-300">รีวิว 2,147+</Link>
          </p>
        </div>
      </section>
          <SiteCoreLinks />
    </main>
  );
}

function FaqItem({ faq }: { faq: FAQ }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-white/[0.05]"
      >
        <span className="text-sm font-semibold text-white">{faq.q}</span>
        <ChevronDown className={`h-4 w-4 flex-none text-amber-300 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="border-t border-white/10 bg-white/[0.02] px-5 py-4 text-sm leading-relaxed text-white/75">{faq.a}</div>}
    </div>
  );
}
